<script lang="ts">
import { browser } from '$app/environment';
import { EVENTS, identifyUser, trackEvent } from '$lib/utils/analytics.js';
import { loadRecaptcha } from '$lib/utils/recaptcha-client';
import { RECAPTCHA_ACTIONS } from '$lib/utils/recaptchaActions';
import { GOOGLE_RECAPTCHA_SITE_KEY } from '../../../config/apiKeys';
import Link from '../../atoms/Link/Link.svelte';
import InputContainer from '../InputContainer/InputContainer.svelte';

interface Props {
    newsletterTitle?: string;
    newsletterText?: string;
    source?: string;
}

type NewsletterSuccessMessage = 'already_subscribed' | 'pending' | 'subscribed';

type NewsletterResponse =
    | {
          success: true;
          message: NewsletterSuccessMessage;
          email: string;
      }
    | {
          error: string;
      };

let { newsletterTitle = '', newsletterText = '', source: sourceOverride = '' }: Props = $props();

let emailValue = $state('');
let errorMessage = $state('');
let successMessage = $state('');

// Capture page context for attribution
let pageContext = $state({
    source: '',
    signupUrl: '',
    utmSource: '',
    utmMedium: '',
    utmCampaign: '',
    utmTerm: '',
    utmContent: '',
    referrer: '',
    tags: [] as string[],
});

// Capture context when component mounts (only in browser)
$effect(() => {
    if (browser) {
        const url = new URL(window.location.href);
        const params = url.searchParams;

        // Determine source - use prop if provided, otherwise detect from pathname
        const pathname = url.pathname;
        let source = sourceOverride || 'website';
        const tags: string[] = ['website-signup'];

        // Add location-specific tag if source prop is provided
        if (sourceOverride) {
            tags.push(sourceOverride);
        }

        // Add page-specific tags
        if (pathname === '/') {
            tags.push('homepage');
            if (!sourceOverride) {
                source = 'homepage';
            }
        } else {
            tags.push('content-page');
            if (!sourceOverride) {
                source = `page${pathname.replace(/\//g, '-')}`;
            }
        }

        // Create clean signup URL without UTM parameters (to avoid duplication)
        const cleanUrl = new URL(window.location.href);
        cleanUrl.searchParams.delete('utm_source');
        cleanUrl.searchParams.delete('utm_medium');
        cleanUrl.searchParams.delete('utm_campaign');
        cleanUrl.searchParams.delete('utm_term');
        cleanUrl.searchParams.delete('utm_content');
        const signupUrl = cleanUrl.toString();

        pageContext = {
            source,
            signupUrl,
            utmSource: params.get('utm_source') || '',
            utmMedium: params.get('utm_medium') || '',
            utmCampaign: params.get('utm_campaign') || '',
            utmTerm: params.get('utm_term') || '',
            utmContent: params.get('utm_content') || '',
            referrer: document.referrer || '',
            tags,
        };
    }
});

function handleSubmit(e: Event) {
    /** https://elliscs.hashnode.dev/build-your-own-sveltekit-boilerplate-using-mailchimp-with-endpoints **/
    e.preventDefault();

    // Track form submission attempt
    trackEvent(EVENTS.FORM_STARTED, {
        form_name: 'newsletter',
        email: emailValue,
        source: pageContext.source,
    });

    // Execute reCAPTCHA before submitting
    doRecaptcha();
}

async function doSubmit(token: string) {
    try {
        const body = {
            emailValue,
            recaptchaToken: token,
            ...pageContext,
        };

        const result = await fetch('/api/newsletter', {
            method: 'POST',
            body: JSON.stringify(body),
        });

        const mailchimpResponse = (await result.json()) as NewsletterResponse;

        if (result.status === 200 && 'message' in mailchimpResponse) {
            // Handle both new subscription and already subscribed
            if (mailchimpResponse.message === 'already_subscribed') {
                successMessage = 'You are already subscribed. Thank you!';
            } else if (mailchimpResponse.message === 'pending') {
                successMessage =
                    'Almost there! Please check your email to confirm your subscription.';
            } else {
                // Fallback for 'subscribed' status (if ever switched to single opt-in)
                successMessage = 'You are now subscribed. Thank you!';
            }
            errorMessage = '';

            // Track successful newsletter signup
            trackEvent(EVENTS.NEWSLETTER_SIGNUP, {
                email: emailValue,
                source: pageContext.source,
                signup_url: pageContext.signupUrl,
                utm_source: pageContext.utmSource,
                utm_medium: pageContext.utmMedium,
                utm_campaign: pageContext.utmCampaign,
                already_subscribed: mailchimpResponse.message === 'already_subscribed',
            });

            // Identify the user in analytics platforms
            identifyUser(emailValue, {
                newsletter_subscriber: true,
                subscription_source: pageContext.source,
                subscription_date: new Date().toISOString(),
            });

            console.log('[Newsletter] Successfully tracked subscription in PostHog and HubSpot');
        } else {
            const submissionError =
                'error' in mailchimpResponse ? mailchimpResponse.error : 'Subscription failed';
            errorMessage = submissionError;

            // Track subscription error
            trackEvent(EVENTS.FORM_ERROR, {
                form_name: 'newsletter',
                error: submissionError,
                email: emailValue,
            });
        }
    } catch (error) {
        errorMessage = 'An error occurred. Please try again.';
        console.error('[Newsletter] Submission error:', error);
        trackEvent(EVENTS.FORM_ERROR, {
            form_name: 'newsletter',
            error: 'network_or_parsing_error',
            email: emailValue,
        });
    }
}

// Execute reCAPTCHA and get token
const doRecaptcha = async () => {
    try {
        const recaptcha = await loadRecaptcha(GOOGLE_RECAPTCHA_SITE_KEY);
        recaptcha.ready(() => {
            recaptcha
                .execute(GOOGLE_RECAPTCHA_SITE_KEY, { action: RECAPTCHA_ACTIONS.newsletter })
                .then((token: string) => {
                    doSubmit(token);
                })
                .catch((error: Error) => {
                    errorMessage = 'Security verification failed. Please try again.';
                    console.error('[Newsletter] reCAPTCHA execution failed:', error);
                    trackEvent(EVENTS.FORM_ERROR, {
                        form_name: 'newsletter',
                        error: 'recaptcha_execution_failed',
                        email: emailValue,
                    });
                });
        });
    } catch (error) {
        errorMessage = 'Security verification unavailable. Please refresh and try again.';
        console.error('[Newsletter] reCAPTCHA script failed to load:', error);
    }
};
</script>

<section class="newsletter">
    {#if !successMessage}
        {#if errorMessage}
            <p class="errorMessage">{errorMessage}</p>
        {/if}
        <h2 class="newsletter__title">{newsletterTitle}</h2>
        <span class="newsletter__tagline">{newsletterText}</span>
        <div class="newsletter__form-container">
            <form class="newsletter__form" onsubmit={handleSubmit}>
                <InputContainer
                    bind:valueObject={emailValue}
                    hidden={false}
                    title="email"
                    id="footer-newsletter-email"
                    required
                    name="email"
                    type="email"
                    placeholder="Add your email"
                    className="newsletter__input"
                />
                <Link type="button" class="button">
                    {#snippet text()}
                        Subscribe
                    {/snippet}
                </Link>
            </form>
        </div>
    {:else}
        <p class="successMessage">{successMessage}</p>
    {/if}
</section>

<style lang="scss">
    @use '/src/scss/global-constants/fonts';

    .newsletter {
        margin-bottom: 3px;

        &__title {
            color: var(--colour-black-out);
            font-size: 2.2rem;
            font-style: normal;
            font-weight: 400;
            line-height: 1.1;
            letter-spacing: -0.066rem;
            padding-bottom: 3.3rem;
        }

        &__tagline {
            display: block;
            font-size: 1.6rem;
            font-style: normal;
            font-weight: 400;
            line-height: 1.3;
            padding-bottom: 2.8rem;
        }

        &__form-container {
            border: 1px solid var(--colour-dreamless-sleep);
            border-radius: 4rem;
            padding: 1rem 0 1rem 1.5rem;
        }

        &__form {
            align-items: stretch;
            display: flex;
            flex-direction: row;
            justify-content: center;
            text-align: center;
        }
    }

    .newsletter__form :global(.button) {
        font-size: 1.6rem;
        text-transform: none;
        width: 16rem;
        background: none;
    }

    .newsletter__form :global(.field) {
        border: none;
        font-size: 1.6rem;
        margin: 0.5rem 0 0 0;
    }

    .newsletter__form :global(*:not(label)) {
        width: 100%;
    }
    .newsletter__form :global(label) {
        top: 6px;
    }

    .errorMessage {
        color: var(--colour-sacrifice-altar);
        margin: 0 0 2rem;
    }
</style>
